/**
 * Quiz Home Page
 * Displays all available quiz types
 */

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, GraduationCap } from 'lucide-react';
import { QuizCard } from '../components';
import { useQuiz } from '../context/QuizContext';
import { SEO, Header, Footer } from '../../../shared/components';

const QuizHomePage: React.FC = () => {
  const navigate = useNavigate();
  const { quizTypes, isLoading, error, loadQuizTypes, startQuiz } = useQuiz();

  useEffect(() => {
    loadQuizTypes();
  }, [loadQuizTypes]);

  const handleStartQuiz = async (quizTypeId: number) => {
    await startQuiz(quizTypeId);
    navigate(`/quiz/take/${quizTypeId}`);
  };

  if (isLoading) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex items-center justify-center">
          <div className="flex items-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading quizzes...</span>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <p className="text-destructive mb-4">{error}</p>
            <button
              onClick={() => loadQuizTypes()}
              className="text-primary hover:underline"
            >
              Try again
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEO
        title="Find Your Perfect University Match"
        description="Take our interactive quizzes to discover universities and programs that match your goals, preferences, and budget."
        keywords="university quiz, course selection, study abroad, university matching"
      />

      <Header />

      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-primary via-emerald-600 to-teal-600 text-white py-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md px-4 py-2 rounded-full mb-6 border border-white/30">
                <GraduationCap className="h-5 w-5 text-white" />
                <span className="text-sm font-bold uppercase tracking-wide">University Matching</span>
              </div>

              <h1 className="text-5xl md:text-6xl font-extrabold mb-6 leading-tight">
                Find Your Perfect University Match
              </h1>
              <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed max-w-3xl mx-auto">
                Answer a few questions and get personalized recommendations for universities and programs that align with your goals, preferences, and budget.
              </p>

              {/* Stats Pills */}
              <div className="flex flex-wrap justify-center gap-4">
                <div className="bg-white/10 backdrop-blur-md border border-white/20 px-6 py-3 rounded-full">
                  <span className="font-bold text-2xl">200+</span>
                  <span className="text-white/80 ml-2">Universities</span>
                </div>
                <div className="bg-white/10 backdrop-blur-md border border-white/20 px-6 py-3 rounded-full">
                  <span className="font-bold text-2xl">86</span>
                  <span className="text-white/80 ml-2">Programs</span>
                </div>
                <div className="bg-white/10 backdrop-blur-md border border-white/20 px-6 py-3 rounded-full">
                  <span className="font-bold text-2xl">10</span>
                  <span className="text-white/80 ml-2">Countries</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quiz Cards Grid */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="max-w-6xl mx-auto">
            <div className="mb-12 text-center">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-100 to-teal-100 dark:from-emerald-950 dark:to-teal-950 px-4 py-2 rounded-full mb-4">
                <span className="text-lg">📝</span>
                <span className="text-sm font-bold text-emerald-700 dark:text-emerald-300 uppercase tracking-wide">Available Quizzes</span>
              </div>
              <h2 className="text-4xl font-bold mb-4 bg-gradient-to-r from-primary to-emerald-600 bg-clip-text text-transparent">
                Choose Your Path
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Select a quiz to get started. Each takes just a few minutes to complete and provides instant, personalized results.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {quizTypes.map(quiz => (
                <QuizCard
                  key={quiz.id}
                  quiz={quiz}
                  onStart={handleStartQuiz}
                />
              ))}
            </div>

            {quizTypes.length === 0 && !isLoading && (
              <div className="text-center py-20">
                <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-emerald-100 to-teal-100 dark:from-emerald-900 dark:to-teal-900 rounded-full flex items-center justify-center">
                  <span className="text-5xl">📝</span>
                </div>
                <h3 className="text-2xl font-bold mb-3 text-foreground">No quizzes available yet</h3>
                <p className="text-lg text-muted-foreground">Check back soon for new quizzes!</p>
              </div>
            )}
          </div>
        </div>

        {/* Features Section */}
        <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 py-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4 bg-gradient-to-r from-primary to-emerald-600 bg-clip-text text-transparent">
                  Why Take Our Quiz?
                </h2>
                <p className="text-muted-foreground text-lg">
                  Get personalized insights in minutes
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="bg-card rounded-2xl p-8 border-2 border-border hover:border-primary transition-all hover:shadow-lg">
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-100 to-teal-100 dark:from-emerald-900 dark:to-teal-900 rounded-2xl flex items-center justify-center mb-6">
                    <span className="text-4xl">🎯</span>
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-3">Personalized Results</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Get tailored university and program recommendations based on your unique preferences, goals, and academic background
                  </p>
                </div>

                <div className="bg-card rounded-2xl p-8 border-2 border-border hover:border-primary transition-all hover:shadow-lg">
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-100 to-teal-100 dark:from-emerald-900 dark:to-teal-900 rounded-2xl flex items-center justify-center mb-6">
                    <span className="text-4xl">⚡</span>
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-3">Quick & Easy</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Complete quizzes in just a few minutes and receive instant results with detailed insights and recommendations
                  </p>
                </div>

                <div className="bg-card rounded-2xl p-8 border-2 border-border hover:border-primary transition-all hover:shadow-lg">
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-100 to-teal-100 dark:from-emerald-900 dark:to-teal-900 rounded-2xl flex items-center justify-center mb-6">
                    <span className="text-4xl">🌍</span>
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-3">Global Coverage</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Explore 200+ universities and 86 programs from 10 countries around the world to find your perfect match
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default QuizHomePage;
